// src/pages/Home/components/AnimatedApplicationName.jsx

import React, { useEffect, useRef, useState } from "react";
import heroApplications from "../data/heroApplications";

const ANIMATION_DURATION = 700;
const DISPLAY_DURATION = 2500;

const cx = (...classes) => classes.filter(Boolean).join(" ");

function ApplicationLogo({ application, className = "" }) {
  if (!application?.logo) return null;

  return (
    <img
      src={application.logo}
      alt=""
      aria-hidden="true"
      className={cx(
        "shrink-0 object-contain drop-shadow-[0_10px_24px_rgba(0,0,0,0.24)]",
        className,
      )}
    />
  );
}

function ApplicationLabel({
  application,
  position = "current",
  isAnimating = false,
}) {
  if (!application) return null;

  const animationClass = isAnimating
    ? position === "previous"
      ? "animate-application-name-exit"
      : "animate-application-name-enter"
    : position === "previous"
      ? "translate-y-0 opacity-100"
      : "translate-y-0 opacity-100";

  return (
    <span
      className={cx(
        "absolute inset-0 flex items-center",
        "will-change-transform",
        animationClass,
      )}
      aria-hidden={position === "previous"}
    >
      <span className="whitespace-nowrap">
        <span className="text-white">{application.prefix}</span>

        <span
          className="transition-colors duration-500"
          style={{
            color: application.color,
            textShadow: `0 0 34px ${application.color}35`,
          }}
        >
          {application.suffix}
        </span>
      </span>
    </span>
  );
}

export default function AnimatedApplicationName({
  interval = DISPLAY_DURATION,
  showLogo = true,
  className = "",
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const animationTimeoutRef = useRef(null);

  const currentApplication = heroApplications[currentIndex];

  const previousApplication =
    previousIndex === null ? null : heroApplications[previousIndex];

  useEffect(() => {
    if (heroApplications.length <= 1) return undefined;

    const rotationInterval = window.setInterval(() => {
      setCurrentIndex((currentValue) => {
        const nextIndex = (currentValue + 1) % heroApplications.length;

        setPreviousIndex(currentValue);
        setIsAnimating(true);

        window.clearTimeout(animationTimeoutRef.current);

        animationTimeoutRef.current = window.setTimeout(() => {
          setIsAnimating(false);
          setPreviousIndex(null);
        }, ANIMATION_DURATION);

        return nextIndex;
      });
    }, interval);

    return () => {
      window.clearInterval(rotationInterval);
      window.clearTimeout(animationTimeoutRef.current);
    };
  }, [interval]);

  return (
    <div
      className={cx(
        "flex min-w-0 items-center gap-3 sm:gap-4 lg:gap-5",
        className,
      )}
    >
      {showLogo ? (
        <div className="relative grid h-12 w-12 shrink-0 place-items-center sm:h-14 sm:w-14 lg:h-[76px] lg:w-[76px]">
          {previousApplication && isAnimating ? (
            <ApplicationLogo
              application={previousApplication}
              className="absolute inset-0 h-full w-full animate-application-logo-exit"
            />
          ) : null}

          <ApplicationLogo
            key={currentApplication.id}
            application={currentApplication}
            className={cx(
              "absolute inset-0 h-full w-full",
              isAnimating
                ? "animate-application-logo-enter"
                : "opacity-100",
            )}
          />
        </div>
      ) : null}

      <div
        className={cx(
          "relative min-w-0 overflow-hidden",
          "h-[1.15em]",
          "text-[clamp(2.65rem,8.5vw,7.6rem)]",
          "font-black leading-none tracking-[-0.055em]",
        )}
      >
        {previousApplication && isAnimating ? (
          <ApplicationLabel
            application={previousApplication}
            position="previous"
            isAnimating
          />
        ) : null}

        <ApplicationLabel
          key={currentApplication.id}
          application={currentApplication}
          position="current"
          isAnimating={isAnimating}
        />

        <span className="invisible whitespace-nowrap" aria-hidden="true">
          MonAppliTournoi
        </span>
      </div>

      <span className="sr-only">
        {currentApplication.prefix}
        {currentApplication.suffix}
      </span>
    </div>
  );
}